import type { StaticPublishDirectory } from "@mph/shared";

export type StaticOutputContractId =
  | "vite"
  | "create-react-app"
  | "astro-static"
  | "next-static-export"
  | "parcel"
  | "sveltekit-static"
  | "react-router-spa"
  | "nuxt-static"
  | "gatsby"
  | "docusaurus"
  | "eleventy"
  | "hugo";

export interface StaticOutputContract {
  id: StaticOutputContractId;
  packageNames: readonly string[];
  publishDirectory: Exclude<StaticPublishDirectory, ".">;
  sourceOnlyMessage: string;
  serverModeMessage?: string;
}

export const HUGO_CONFIG_FILENAMES = Object.freeze([
  "hugo.toml",
  "hugo.yaml",
  "hugo.yml",
  "hugo.json",
  "config.toml",
  "config.yaml",
  "config.yml",
  "config.json",
] as const);

export const HUGO_STATIC_OUTPUT_CONTRACT: StaticOutputContract = Object.freeze({
  id: "hugo",
  packageNames: [],
  publishDirectory: "public",
  sourceOnlyMessage: "This Hugo project needs a deterministic Hugo Extended build before Doorway can publish its public folder.",
});

export const STATIC_OUTPUT_CONTRACTS: readonly StaticOutputContract[] = Object.freeze([
  {
    id: "sveltekit-static",
    packageNames: ["@sveltejs/kit", "@sveltejs/adapter-static"],
    publishDirectory: "build",
    sourceOnlyMessage: "This SvelteKit project needs a static build. Run the build locally with adapter-static and select the project with its build folder.",
    serverModeMessage: "This SvelteKit project needs a server runtime. Configure adapter-static and create a build folder before publishing.",
  },
  {
    id: "react-router-spa",
    packageNames: ["@react-router/dev"],
    publishDirectory: "build/client",
    sourceOnlyMessage: "This React Router project needs an SPA build. Set ssr: false, run the build locally, and select the project with its build/client folder.",
    serverModeMessage: "This React Router build includes a server runtime. Set ssr: false and create a client-only build before publishing.",
  },
  {
    id: "nuxt-static",
    packageNames: ["nuxt"],
    publishDirectory: ".output/public",
    sourceOnlyMessage: "This Nuxt project needs a generated static site. Run nuxt generate locally and select the project with its .output/public folder.",
  },
  {
    id: "create-react-app",
    packageNames: ["react-scripts"],
    publishDirectory: "build",
    sourceOnlyMessage: "This Create React App project needs a build. Run the build locally and select the project with its build folder.",
  },
  {
    id: "astro-static",
    packageNames: ["astro"],
    publishDirectory: "dist",
    sourceOnlyMessage: "This Astro project needs a static build. Run the build locally and select the project with its dist folder.",
    serverModeMessage: "This Astro project is configured for server output. Choose a static Astro build with a dist folder.",
  },
  {
    id: "next-static-export",
    packageNames: ["next"],
    publishDirectory: "out",
    sourceOnlyMessage: "This Next.js static export needs a build. Run next build with output: 'export' and select the project with its out folder.",
    serverModeMessage: "This Next.js project needs a server runtime. Export it as a static site to create an out folder.",
  },
  {
    id: "parcel",
    packageNames: ["parcel"],
    publishDirectory: "dist",
    sourceOnlyMessage: "This Parcel project needs a production build. Run the build locally and select the project with its dist folder.",
  },
  {
    id: "gatsby",
    packageNames: ["gatsby"],
    publishDirectory: "public",
    sourceOnlyMessage: "This Gatsby project needs a static build. Run gatsby build locally and select the project with its public folder.",
  },
  {
    id: "docusaurus",
    packageNames: ["@docusaurus/core"],
    publishDirectory: "build",
    sourceOnlyMessage: "This Docusaurus project needs a static build. Run the build locally and select the project with its build folder.",
  },
  {
    id: "eleventy",
    packageNames: ["@11ty/eleventy"],
    publishDirectory: "_site",
    sourceOnlyMessage: "This Eleventy project needs a static build. Run the build locally and select the project with its _site folder.",
  },
  {
    id: "vite",
    packageNames: ["vite"],
    publishDirectory: "dist",
    sourceOnlyMessage: "This Vite project needs a build. Run the build locally and select the project with its dist folder.",
  },
  HUGO_STATIC_OUTPUT_CONTRACT,
]);

export function staticOutputContractForPackages(packageNames: ReadonlySet<string>): StaticOutputContract | undefined {
  return STATIC_OUTPUT_CONTRACTS.find((contract) => (
    contract.packageNames.length > 0 && contract.packageNames.every((name) => packageNames.has(name))
  ));
}
